import React, { useEffect, useMemo, useRef } from 'react';
import { isEmpty } from 'lodash';
import { Col, Container, Row } from 'react-bootstrap';

import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { usePOMGroupState } from './PomGroupContext';
import * as action from '../../Store/Pom.action';

function TopPomGroupBar() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { pomGroupState, setPomGroupState, loggedInUserId } = usePOMGroupState();
  const { selectCategoryOne, selectCategoryTwo, selectCategoryThree, selectCategoryFour } = pomGroupState;
  const didInitRefResetC3 = useRef(false);
  const didInitRefResetC4 = useRef(false);

  // Helpers
  const toIdArray = (val) => {
    if (!Array.isArray(val)) return [];
    return val.map((v) => (typeof v === 'object' ? v?._id : v)).filter(Boolean);
  };
  const ensureOptionShape = (arr) => {
    const list = Array.isArray(arr) ? arr : [];
    return list
      .map((o) => ({ _id: o?._id || o?.value, name: o?.name || o?.label, label: o?.name || o?.label, value: o?._id || o?.value }))
      .filter((x) => x?._id);
  };

  const selectedC1 = useMemo(() => ensureOptionShape(selectCategoryOne), [selectCategoryOne]);
  const selectedC2 = useMemo(() => ensureOptionShape(selectCategoryTwo), [selectCategoryTwo]);
  const selectedC3 = useMemo(() => ensureOptionShape(selectCategoryThree), [selectCategoryThree]);
  const selectedC4 = useMemo(() => ensureOptionShape(selectCategoryFour), [selectCategoryFour]);

  useEffect(() => {
    if (!isEmpty(selectCategoryOne) && !isEmpty(selectCategoryTwo)) {
      const queryParams = {
        isActive: 1,
        loggedInUserId,
        categoryOneId: toIdArray(selectCategoryOne),
        categoryTwoId: toIdArray(selectCategoryTwo),
        categoryType: 3,
      };
      if (pomGroupState?.catBasedOnPTData) {
        queryParams.productTypeId = pomGroupState?.productTypeId || [];
      }
      dispatch(action.fetchCategoryThreeForGrading(queryParams));
    }
  }, [selectCategoryOne, selectCategoryTwo, loggedInUserId, dispatch, pomGroupState?.catBasedOnPTData, pomGroupState?.productTypeId]);

  useEffect(() => {
    if (!isEmpty(selectCategoryOne) && !isEmpty(selectCategoryTwo) && !isEmpty(selectCategoryThree)) {
      const queryParams = {
        isActive: 1,
        loggedInUserId,
        categoryOneId: toIdArray(selectCategoryOne),
        categoryTwoId: toIdArray(selectCategoryTwo),
        categoryThreeId: toIdArray(selectCategoryThree),
        categoryType: 4,
      };
      if (pomGroupState?.catBasedOnPTData) {
        queryParams.productTypeId = pomGroupState?.productTypeId || [];
      }
      dispatch(action.fetchCategoryFour(queryParams));
    }
  }, [selectCategoryOne, selectCategoryTwo, selectCategoryThree, loggedInUserId, dispatch, pomGroupState?.catBasedOnPTData, pomGroupState?.productTypeId]);

  useEffect(() => {
    if (!didInitRefResetC3.current) {
      didInitRefResetC3.current = true;
      return;
    }
    if (isEmpty(selectCategoryOne) || isEmpty(selectCategoryTwo)) {
      setPomGroupState((prev) => ({
        ...prev,
        CategoryThreeData: [],
        selectCategoryThree: [],
        selectCategoryFour: [],
        categoryFourData: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCategoryOne, selectCategoryTwo]);

  useEffect(() => {
    if (!didInitRefResetC4.current) {
      didInitRefResetC4.current = true;
      return;
    }
    if (isEmpty(selectCategoryOne) || isEmpty(selectCategoryTwo) || isEmpty(selectCategoryThree)) {
      setPomGroupState((prev) => ({
        ...prev,
        selectCategoryFour: [],
        categoryFourData: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCategoryOne, selectCategoryTwo, selectCategoryThree]);

  const handleSelectChange = async (value, keyName) => {
    setPomGroupState((prev) => {
      const next = { ...prev, [keyName]: value };
      if (keyName === 'selectCategoryOne' || keyName === 'selectCategoryTwo') {
        next.selectCategoryThree = [];
        next.CategoryThreeData = [];
        next.selectCategoryFour = [];
        next.categoryFourData = [];
      }
      return next;
    });
  };

  return (
    <Container fluid className="mt-24 pom-group">
      <div className="form-container mb-0">
        <div className="flex-fill">
          <Row>
            <Col md={2}>
              <div className="common-input-control mb-0">
                <p className="field-name">
                  {t('USER_GROUP_VIEW.NAME')} <span className="asterisk">*</span>
                </p>
                <input
                  autoComplete="off"
                  type="text"
                  value={pomGroupState?.groupName}
                  name={t('POM_GROUP.GROUP')}
                  className="form-control"
                  onChange={(e) => handleSelectChange(e.target.value.trimStart(), 'groupName')}
                />
              </div>

              {pomGroupState?.showError && !pomGroupState?.groupName && <small className="error-message">{t('POM_GROUP.GROUP_NAME')}</small>}
            </Col>

            {pomGroupState?.catBasedOnPTData && (
              <Col md={2}>
                <div className="common-react-select mb-0">
                  <p className="asterisk-req field-name">Product Type</p>
                  <MultiSelect
                    name="productType"
                    options={pomGroupState?.productTypeData}
                    value={(pomGroupState?.productTypeData || []).filter((opt) => (pomGroupState?.productTypeId || []).includes(opt?._id))}
                    labelledBy="Product Type"
                    onChange={(selected) => handleSelectChange(Array.isArray(selected) ? selected.map((val) => val?._id) : [], 'productTypeId')}
                    className="product-custom-multiselect common-new-multiselect"
                    placeholder={t('COLOUR_LIBRARYS.COLOUR_LIBRARY_GROUP.ADD_EDIT_COLOUR_GROUP.PRODUCT_TYPE')}
                  />
                </div>
              </Col>
            )}
            <Col md={2}>
              <div className="common-react-select mb-0">
                <p className="asterisk-req field-name">{t('SPEC_TEMP_LIBRARY.CATEGORY1')}</p>
                <MultiSelect
                  name="CategoryOne"
                  options={pomGroupState?.CategoryOneData}
                  value={selectedC1}
                  labelledBy="Brand Not selected"
                  onChange={(selected) => handleSelectChange(ensureOptionShape(selected), 'selectCategoryOne')}
                  className="product-custom-multiselect common-new-multiselect"
                  placeholder={t('POM_GROUP.SELECT_CATEGORY1')}
                />
              </div>
            </Col>

            <Col md={2}>
              <div className="common-react-select mb-0">
                <p className="asterisk-req field-name"> {t('SPEC_TEMP_LIBRARY.CATEGORY2')}</p>
                <MultiSelect
                  name="CategoryTwo"
                  options={pomGroupState?.CategoryTwoData}
                  className="product-custom-multiselect common-new-multiselect"
                  value={selectedC2}
                  labelledBy="Brand Not selected"
                  onChange={(selected) => handleSelectChange(ensureOptionShape(selected), 'selectCategoryTwo')}
                  placeholder={t('POM_GROUP.SELECT_CATEGORY2')}
                />
              </div>
            </Col>

            <Col md={2}>
              <div className="common-react-select mb-0">
                <p className="asterisk-req field-name"> {t('SPEC_TEMP_LIBRARY.CATEGORY3')}</p>
                <MultiSelect
                  name="CategoryThree"
                  options={pomGroupState?.CategoryThreeData}
                  className="product-custom-multiselect common-new-multiselect"
                  value={selectedC3}
                  labelledBy="Brand Not selected"
                  onChange={(selected) => handleSelectChange(ensureOptionShape(selected), 'selectCategoryThree')}
                  placeholder={t('POM_GROUP.SELECT_CATEGORY3')}
                />
              </div>
            </Col>

            <Col md={2}>
              <div className="common-react-select mb-0">
                <p className="asterisk-req field-name"> {t('SPEC_TEMP_LIBRARY.CATEGORY4')}</p>
                <MultiSelect
                  name="CategoryFour"
                  options={pomGroupState?.categoryFourData}
                  className="product-custom-multiselect common-new-multiselect"
                  value={selectedC4}
                  labelledBy="Category Four Not selected"
                  onChange={(selected) => handleSelectChange(ensureOptionShape(selected), 'selectCategoryFour')}
                  placeholder={t('POM_GROUP.SELECT_CATEGORY4')}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
}

export default TopPomGroupBar;
